import React, { useEffect } from 'react'
import { useForm, type SubmitHandler, Controller } from 'react-hook-form'

import { Input, InputGroup } from '@components'
import Button from '@components/Button'
import FileInput from '@components/FileInput'
import Modal from '@components/Modal'
import { successMessages } from '@constants/feedbackMessages'
import { Dialog } from '@headlessui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { pushNotificationsApi } from '@services/PushNotificationsApi'
import { closeModal, openAlertModal } from '@store/ducks/modal'
import { notificationCreators } from '@store/ducks/notification'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { inputIsValid } from '@utils/helpers'
import { getCssStyle, pixelsToRem } from '@utils/helpers/styleHelpers'
import * as yup from 'yup'

const addPushNotificationSchema = yup.object({
	title: yup.string().required('Título é obrigatório'),
	description: yup.string().required('Descrição é obrigatória'),
	purchaseUrl: yup
		.string()
		.url('Deve ser uma URL válida')
		.required('Link de compra é obrigatório'),
	image: yup
		.mixed<File>()
		.test('fileSize', 'Imagem deve ter no máximo 2MB', (file) => {
			if (!file) return true

			return file.size <= 2 * 1024 * 1024
		})
		.test('fileType', 'Formato inválido', (file) => {
			if (!file) return true

			return ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'].includes(
				file.type
			)
		})
		.required('Imagem é obrigatória'),
})

type Inputs = {
	title: string
	description: string
	purchaseUrl: string
	image: File
}

const styles = {
	container:
		'relative inline-block align-bottom bg-white rounded-lg pl-10 pr-10 pt-6 pb-9 text-left overflow-hidden shadow-xl transform transition-all my-8 sm:align-middle',
	title: 'text-lg leading-6 font-medium text-gray-900',
	subTitle: 'text-gray-500 mt-1',
	buttonGroup: 'mt-7 flex gap-4 justify-end',
}

export default function AddPushNotification() {
	const dispatch = useAppDispatch()
	const {
		modal: { modalName },
	} = useAppSelector((state) => state)

	const [addPushNotification, { isLoading, isSuccess }] =
		pushNotificationsApi.useAddMutation()

	const {
		register,
		control,
		handleSubmit,
		reset,
		setValue,
		formState: { errors, touchedFields },
	} = useForm<Inputs>({
		mode: 'onBlur',
		resolver: yupResolver(addPushNotificationSchema),
	})

	const onSubmit: SubmitHandler<Inputs> = (data) => {
		const approvePushNotification = {
			title: `Enviar notificação ${data.title}`,
			message: `Tem certeza que deseja enviar a notificação ${data.title} para todos os usuários sem um plano pago ativo?`,
			buttonLabel: 'Enviar notificação',
			onClick: () =>
				addPushNotification({
					title: data.title,
					description: data.description,
					purchaseUrl: data.purchaseUrl,
					image: data.image,
				}),
		}

		dispatch(openAlertModal(approvePushNotification))
	}

	useEffect(() => {
		if (isSuccess) {
			dispatch(closeModal())
			dispatch(
				notificationCreators.show(successMessages.storedPushNotification)
			)
			reset()
		}
	}, [isSuccess, dispatch, reset])

	useEffect(() => {
		if (!modalName) reset()
	}, [modalName, reset])

	return (
		<Modal name='addPushNotification'>
			<Dialog.Panel
				style={getCssStyle(
					{ name: 'overflowY', value: 'auto' },
					{ name: 'maxWidth', value: pixelsToRem(630) },
					{ name: 'maxHeight', value: pixelsToRem(600) },
					{ name: 'width', value: '100%' }
				)}
				className={`${styles.container} modal-scroll`}
			>
				<p className={styles.title}>Adicionar Notificação Push</p>
				<p className={styles.subTitle}>
					Preencha os campos abaixo para adicionar uma nova notificação push.
				</p>

				<form onSubmit={handleSubmit(onSubmit)}>
					<InputGroup className='mt-4' label='Título' id='title'>
						<Input
							type='text'
							isValid={inputIsValid(errors.title, touchedFields.title)}
							{...register('title')}
							placeholder='Digite o título da notificação'
						/>
					</InputGroup>

					<InputGroup className='mt-4' label='Descrição' id='description'>
						<Input
							type='text'
							isValid={inputIsValid(
								errors.description,
								touchedFields.description
							)}
							{...register('description')}
							placeholder='Digite a descrição'
						/>
					</InputGroup>

					<InputGroup className='mt-4' label='Link de compra' id='purchaseUrl'>
						<Input
							isValid={inputIsValid(
								errors.purchaseUrl,
								touchedFields.purchaseUrl
							)}
							{...register('purchaseUrl')}
							type='url'
							placeholder='https://exemplo.com'
						/>
					</InputGroup>

					<Controller
						control={control}
						name='image'
						render={({ field, fieldState: { error } }) => (
							<InputGroup className='mt-4' label='Imagem' id='image'>
								<>
									<FileInput
										mt='mt-4'
										onChange={(files) => {
											if (files && files[0]) {
												setValue('image', files[0])
												field.onChange(files[0])
											}
										}}
										accept='image/png, image/jpeg, image/jpg, image/gif'
										isValid={!error}
									>
										Selecionar Imagem
									</FileInput>
									{error && (
										<span className='text-red-500 text-sm mt-1'>
											{error.message}
										</span>
									)}
								</>
							</InputGroup>
						)}
					/>

					<div className={styles.buttonGroup}>
						<Button
							type='button'
							onClick={() => dispatch(closeModal())}
							variant='outline'
							disabled={false}
						>
							Cancelar
						</Button>

						<Button type='submit' isLoading={isLoading} disabled={!modalName}>
							Adicionar Notificação
						</Button>
					</div>
				</form>
			</Dialog.Panel>
		</Modal>
	)
}
